/**
 * Cart API Utilities
 * Loaded FIRST — provides shared functions used by cart.js, cart-drawer.js, and page scripts.
 */

/* ==========================================================================
   Cart Count
   ========================================================================== */

/**
 * Update cart count badge in header
 * @param {number} count - New cart item count
 */
function updateCartCount(count) {
  const counters = document.querySelectorAll('.cart-counter, .header__cart-badge');
  counters.forEach(counter => {
    counter.textContent = count;

    // Hide badge when count is 0
    if (counter.classList.contains('header__cart-badge')) {
      counter.classList.toggle('header__cart-badge--hidden', count === 0);
    }
  });
}

/**
 * Format money using Shopify's money format
 */
var Shopify = Shopify || {};
Shopify.money_format = Shopify.money_format || '{{amount}}';

Shopify.formatMoney = Shopify.formatMoney || function(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'undefined' ? 2 : precision;
    thousands = typeof thousands === 'undefined' ? ',' : thousands;
    decimal = typeof decimal === 'undefined' ? '.' : decimal;

    if (isNaN(number) || number == null) return 0;

    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsStr = parts[1] ? (decimal + parts[1]) : '';
    return dollars + centsStr;
  }

  var match = formatString.match(placeholderRegex);
  if (!match) return formatWithDelimiters(cents, 2);

  var value = '';
  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

/* ==========================================================================
   Add to Cart
   ========================================================================== */

/**
 * Add items to cart and open the cart drawer with updated contents.
 * Uses Shopify's sections parameter for a single-fetch flow.
 * Queues requests — only one add-to-cart can run at a time.
 * @param {Array} items - Array of { id, quantity, properties?, selling_plan? } objects
 * @returns {Promise<Object>} - Cart API response (added items)
 */
let _addToCartPending = null;

async function addToCart(items) {
  if (!items || !Array.isArray(items) || items.length === 0) {
    throw new Error('Invalid items array');
  }

  // Queue: wait for any pending add-to-cart to finish
  if (_addToCartPending) {
    await _addToCartPending.catch(() => {});
  }

  const promise = _doAddToCart(items);
  _addToCartPending = promise;
  try {
    return await promise;
  } finally {
    if (_addToCartPending === promise) _addToCartPending = null;
  }
}

async function _doAddToCart(items) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, sections: 'cart-drawer' })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[cart-api] /cart/add.js failed', {
      status: response.status,
      payload: items,
      errorData
    });
    throw new Error(errorData.description || errorData.message || `Cart API error: ${response.status}`);
  }

  const data = await response.json();

  // Render cart drawer with section HTML from response
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    await customElements.whenDefined('cart-drawer');
    cartDrawer.renderContents(data);
  }

  return data;
}

// Export globally
window.addToCart = addToCart;
window.updateCartCount = updateCartCount;
